import { useState, useEffect, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { Search, ShoppingCart, Trash2, Minus, Plus, Pencil, Eye, Ban } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, fmtDate } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ReceiptModal } from "../components/ReceiptModal";

export default function POS() {
  const { t } = useLang();
  const { outlet } = useOutletContext() || {};
  const [catalog, setCatalog] = useState({ products: [], categories: [] });
  const [settings, setSettings] = useState({ tax_pct: 0, service_pct: 0, rounding: 0 });
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kqz_cart") || "[]"); } catch { return []; }
  });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [trxDate, setTrxDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [paidAmount, setPaidAmount] = useState("");
  const [orderType, setOrderType] = useState("dine_in");
  const [receiptId, setReceiptId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deliveryApps, setDeliveryApps] = useState([]);
  const [deliveryAppId, setDeliveryAppId] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [editTrx, setEditTrx] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const { user } = useAuth();
  const isAdmin = ["owner", "admin"].includes(user?.role);

  useEffect(() => {
    api.get("/pos/catalog").then((r) => { setCatalog(r.data); setDeliveryApps(r.data.delivery_apps || []); }).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    loadTrx();
  }, []);

  const loadTrx = () => api.get("/pos/transactions").then((r) => setTransactions(r.data)).catch(() => {});

  const openEditTrx = (t0) => {
    setEditTrx(t0);
    setEditForm({
      items: t0.items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
      discount: t0.discount || 0, payment_method: t0.payment_method || "cash",
      paid_amount: t0.paid_amount || 0, order_type: t0.order_type || "dine_in",
      delivery_app_id: t0.delivery_app_id || "",
      trx_date: (t0.created_at || "").slice(0, 10),
    });
  };

  const saveEditTrx = async () => {
    try {
      await api.put(`/pos/transactions/${editTrx.id}`, editForm);
      toast.success("OK — stok & profit dihitung ulang");
      setEditTrx(null);
      loadTrx();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const voidTrx = async (t0) => {
    if (!window.confirm(`${t("voidRefund")} ${t0.trx_number}?`)) return;
    try { await api.post(`/pos/transactions/${t0.id}/void`); toast.success("OK — refunded, stok dikembalikan"); loadTrx(); } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteTrx = async (t0) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/pos/transactions/${t0.id}`); toast.success("OK — stok dikembalikan"); loadTrx(); } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => { localStorage.setItem("kqz_cart", JSON.stringify(cart)); }, [cart]);

  const products = useMemo(() => catalog.products.filter((p) =>
    (activeCat === "all" || p.category_id === activeCat) &&
    (!q || p.name.toLowerCase().includes(q.toLowerCase()))
  ), [catalog, activeCat, q]);

  const addToCart = (p) => {
    setCart((c) => {
      const ex = c.find((i) => i.product_id === p.id);
      if (ex) return c.map((i) => i.product_id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { product_id: p.id, name: p.name, price: p.price, image: p.image, qty: 1 }];
    });
  };

  const setQty = (pid, qty) => {
    if (qty <= 0) return setCart((c) => c.filter((i) => i.product_id !== pid));
    setCart((c) => c.map((i) => i.product_id === pid ? { ...i, qty } : i));
  };

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const taxable = Math.max(subtotal - (discount || 0), 0);
  const taxAmt = taxable * (settings.tax_pct || 0);
  const svcAmt = taxable * (settings.service_pct || 0);
  const selApp = deliveryApps.find((a) => a.id === deliveryAppId);
  const appFee = orderType === "delivery" && selApp ? taxable * (selApp.fee_pct || 0) / 100 + (selApp.fee_fixed || 0) : 0;
  const overheadAmt = taxable * (settings.overhead_pct || 0);
  let total = taxable + taxAmt + svcAmt;
  if (paymentMethod === "cash" && settings.rounding > 0) total = Math.round(total / settings.rounding) * settings.rounding;
  const change = Math.max((Number(paidAmount) || total) - total, 0);

  const checkout = async () => {
    setBusy(true);
    try {
      const res = await api.post("/pos/transactions", {
        items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        outlet_id: outlet || "", discount: Number(discount) || 0,
        payment_method: paymentMethod, paid_amount: Number(paidAmount) || 0, order_type: orderType,
        delivery_app_id: orderType === "delivery" ? deliveryAppId : "",
        trx_date: trxDate || "",
      });
      toast.success(`${res.data.trx_number} — ${fmtIDR(res.data.total)}`);
      setCart([]);
      setDiscount(0);
      setPaidAmount("");
      setTrxDate("");
      setCheckoutOpen(false);
      setReceiptId(res.data.id);
      loadTrx();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const posGrid = (
    <div data-testid="pos-page" className="grid lg:grid-cols-[1fr_360px] gap-4">
      <div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="pos-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="pl-9" />
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-3 mb-1">
          <button data-testid="pos-cat-all" onClick={() => setActiveCat("all")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${activeCat === "all" ? "bg-amber-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
            {t("all")}
          </button>
          {catalog.categories.filter((c) => catalog.products.some((p) => p.category_id === c.id)).map((c) => (
            <button key={c.id} data-testid={`pos-cat-${c.id}`} onClick={() => setActiveCat(c.id)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${activeCat === c.id ? "bg-amber-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              {c.name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          {products.map((p) => (
            <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addToCart(p)}
              className="bg-card border border-border rounded-xl overflow-hidden text-left hover:shadow-md hover:border-amber-400 transition-all active:scale-[0.98]">
              {p.image ? <img src={p.image} alt={p.name} className="h-24 w-full object-cover" /> : <div className="h-24 bg-muted" />}
              <div className="p-3">
                <div className="text-sm font-semibold truncate">{p.name}</div>
                <div className="text-xs font-mono text-amber-600 font-bold">{fmtIDR(p.price)}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-xs flex flex-col max-h-[calc(100vh-7rem)] sticky top-20">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <ShoppingCart className="w-4 h-4 text-amber-600" />
          <span className="font-heading font-semibold">{t("cart")}</span>
          <span className="ml-auto text-xs font-mono text-muted-foreground" data-testid="pos-cart-count">{cart.length} item</span>
          {cart.length > 0 && <button data-testid="pos-clear-cart-btn" onClick={() => setCart([])} className="text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {cart.length === 0 && <div className="text-center text-muted-foreground text-sm py-10">{t("empty")}</div>}
          {cart.map((i) => (
            <div key={i.product_id} data-testid={`pos-cart-item-${i.product_id}`} className="flex items-center gap-2 bg-muted/40 rounded-lg p-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{i.name}</div>
                <div className="text-xs font-mono text-muted-foreground">{fmtIDR(i.price)}</div>
              </div>
              <div className="flex items-center gap-1">
                <button data-testid={`pos-qty-minus-${i.product_id}`} onClick={() => setQty(i.product_id, i.qty - 1)} className="w-6 h-6 rounded-md bg-card border border-border flex items-center justify-center hover:bg-muted"><Minus className="w-3 h-3" /></button>
                <span className="w-8 text-center text-sm font-mono font-semibold">{i.qty}</span>
                <button data-testid={`pos-qty-plus-${i.product_id}`} onClick={() => setQty(i.product_id, i.qty + 1)} className="w-6 h-6 rounded-md bg-card border border-border flex items-center justify-center hover:bg-muted"><Plus className="w-3 h-3" /></button>
              </div>
              <div className="w-20 text-right text-sm font-mono font-bold">{fmtIDR(i.price * i.qty)}</div>
            </div>
          ))}
        </div>
        <div className="border-t border-border p-4 space-y-1.5">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("subtotal")}</span><span className="font-mono" data-testid="pos-subtotal">{fmtIDR(subtotal)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("discount")}</span><span className="font-mono text-rose-600">-{fmtIDR(discount || 0)}</span></div>
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("tax")} + {t("service")}</span><span className="font-mono">{fmtIDR(taxAmt + svcAmt)}</span></div>
          <div className="flex justify-between font-bold text-lg border-t border-border pt-2"><span>{t("total")}</span><span className="font-mono text-amber-600" data-testid="pos-total">{fmtIDR(total)}</span></div>
          <Button data-testid="pos-checkout-btn" disabled={cart.length === 0} onClick={() => setCheckoutOpen(true)} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-5 mt-2">
            {t("checkout")} · {fmtIDR(total)}
          </Button>
        </div>
      </div>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md" data-testid="pos-checkout-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("checkout")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Order Type</Label>
              <Select value={orderType} onValueChange={setOrderType}>
                <SelectTrigger data-testid="pos-order-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dine_in">Dine In</SelectItem>
                  <SelectItem value="takeaway">Takeaway</SelectItem>
                  <SelectItem value="delivery">Delivery</SelectItem>
                </SelectContent>
              </Select></div>
            {orderType === "delivery" && (
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("deliveryApp")}</Label>
                <Select value={deliveryAppId} onValueChange={setDeliveryAppId}>
                  <SelectTrigger data-testid="pos-delivery-app-select" className="mt-1.5"><SelectValue placeholder="Pilih aplikasi..." /></SelectTrigger>
                  <SelectContent>{deliveryApps.filter((a) => a.active !== false).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} (fee {a.fee_pct}%)</SelectItem>)}</SelectContent>
                </Select></div>
            )}
            {isAdmin && (
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Tanggal Transaksi (backdate)</Label>
                <Input data-testid="pos-backdate-input" type="date" value={trxDate} onChange={(e) => setTrxDate(e.target.value)} max={new Date().toISOString().slice(0, 10)} className="mt-1.5" /></div>
            )}
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("discount")} (Rp)</Label>
              <Input data-testid="pos-discount-input" type="number" value={discount} onChange={(e) => setDiscount(Number(e.target.value))} className="mt-1.5" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("paymentMethod")}</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="pos-payment-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t("cash")}</SelectItem>
                  <SelectItem value="qris">QRIS</SelectItem>
                  <SelectItem value="debit">Debit/Credit</SelectItem>
                  <SelectItem value="ewallet">E-Wallet</SelectItem>
                </SelectContent>
              </Select></div>
            {paymentMethod === "cash" && (
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("paid")}</Label>
                <Input data-testid="pos-paid-input" type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} className="mt-1.5 font-mono" /></div>
            )}
            <div className="bg-muted/40 rounded-xl p-4 space-y-1.5">
              <div className="flex justify-between font-bold text-lg"><span>{t("total")}</span><span className="font-mono text-amber-600" data-testid="checkout-total">{fmtIDR(total)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">{t("change")}</span><span className="font-mono font-bold text-emerald-600" data-testid="checkout-change">{fmtIDR(change)}</span></div>
            </div>
            {(appFee > 0 || overheadAmt > 0) && (
              <div className="text-xs border border-dashed border-border rounded-lg px-3 py-2 text-muted-foreground" data-testid="checkout-cost-note">
                <span className="font-semibold">Info internal — tidak dibebankan ke pelanggan, hanya pengurang profit:</span>
                {appFee > 0 && <span className="block mt-1">{t("appFee")} {selApp?.name}: <span className="font-mono">{fmtIDR(appFee)}</span></span>}
                {overheadAmt > 0 && <span className="block">{t("overheadPct")}: <span className="font-mono">{fmtIDR(overheadAmt)}</span></span>}
              </div>
            )}
            <Button data-testid="pos-pay-btn" disabled={busy} onClick={checkout} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-5">
              {busy ? t("loading") : `${t("checkout")} — ${fmtIDR(total)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReceiptModal trxId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} />
    </div>
  );

  const historyTab = (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs" data-testid="trx-history-tab">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted/50">
            {[t("date"), "No. Trx", "Items", t("total"), t("appFee"), "Profit", t("paymentMethod"), "Type", t("actions")].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
          </tr></thead>
          <tbody>
            {transactions.map((t0) => (
              <tr key={t0.id} data-testid={`trx-row-${t0.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(t0.created_at)}</td>
                <td className="px-4 py-2.5 font-mono font-semibold">{t0.trx_number}{t0.status === "refunded" && <span className="ml-1.5 bg-rose-50 dark:bg-rose-950/50 text-rose-600 border border-rose-200 dark:border-rose-800 px-1.5 py-0.5 rounded-full text-[10px] font-sans">{t("refunded")}</span>}</td>
                <td className="px-4 py-2.5 text-muted-foreground max-w-[220px] truncate">{t0.items.map((i) => `${i.name} x${i.qty}`).join(", ")}</td>
                <td className="px-4 py-2.5 font-mono">{fmtIDR(t0.total)}</td>
                <td className="px-4 py-2.5 font-mono text-rose-600">{t0.app_fee ? `-${fmtIDR(t0.app_fee)}` : "-"}</td>
                <td className="px-4 py-2.5 font-mono text-emerald-600">{fmtIDR(t0.profit)}</td>
                <td className="px-4 py-2.5 uppercase text-xs">{t0.payment_method}</td>
                <td className="px-4 py-2.5 text-xs">{t0.order_type}{t0.delivery_app_name ? ` · ${t0.delivery_app_name}` : ""}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <button data-testid={`trx-receipt-${t0.id}`} onClick={() => setReceiptId(t0.id)} className="p-1.5 text-muted-foreground hover:text-foreground"><Eye className="w-4 h-4" /></button>
                  {t0.status !== "refunded" && (
                    <>
                      <button data-testid={`trx-edit-${t0.id}`} onClick={() => openEditTrx(t0)} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil className="w-4 h-4" /></button>
                      <button data-testid={`trx-void-${t0.id}`} onClick={() => voidTrx(t0)} title={t("voidRefund")} className="p-1.5 text-muted-foreground hover:text-amber-600"><Ban className="w-4 h-4" /></button>
                      <button data-testid={`trx-delete-${t0.id}`} onClick={() => deleteTrx(t0)} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {transactions.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">-</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );

  const editDialog = (
    <Dialog open={!!editTrx} onOpenChange={() => setEditTrx(null)}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="trx-edit-dialog">
        <DialogHeader><DialogTitle className="font-heading">{t("edit")} {editTrx?.trx_number}</DialogTitle></DialogHeader>
        {editForm && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Items</Label>
                <Button size="sm" variant="outline" data-testid="trx-edit-add-item" onClick={() => setEditForm({ ...editForm, items: [...editForm.items, { product_id: "", qty: 1 }] })}><Plus className="w-3.5 h-3.5" /></Button>
              </div>
              <div className="space-y-2">
                {editForm.items.map((it, idx) => (
                  <div key={it.product_id || `empty-${idx}`} className="flex items-center gap-2">
                    <Select value={it.product_id} onValueChange={(v) => { const arr = [...editForm.items]; arr[idx] = { ...arr[idx], product_id: v }; setEditForm({ ...editForm, items: arr }); }}>
                      <SelectTrigger data-testid={`trx-edit-product-${idx}`} className="flex-1"><SelectValue placeholder="..." /></SelectTrigger>
                      <SelectContent>{catalog.products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {fmtIDR(p.price)}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input data-testid={`trx-edit-qty-${idx}`} type="number" value={it.qty} onChange={(e) => { const arr = [...editForm.items]; arr[idx] = { ...arr[idx], qty: Number(e.target.value) }; setEditForm({ ...editForm, items: arr }); }} className="w-20" />
                    <button onClick={() => setEditForm({ ...editForm, items: editForm.items.filter((_, i) => i !== idx) })} className="p-1.5 text-muted-foreground hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("discount")} (Rp)</Label>
                <Input data-testid="trx-edit-discount" type="number" value={editForm.discount} onChange={(e) => setEditForm({ ...editForm, discount: Number(e.target.value) })} className="mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("paymentMethod")}</Label>
                <Select value={editForm.payment_method} onValueChange={(v) => setEditForm({ ...editForm, payment_method: v })}>
                  <SelectTrigger data-testid="trx-edit-payment" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t("cash")}</SelectItem>
                    <SelectItem value="qris">QRIS</SelectItem>
                    <SelectItem value="debit">Debit/Credit</SelectItem>
                    <SelectItem value="ewallet">E-Wallet</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Tanggal (backdate)</Label>
                <Input data-testid="trx-edit-backdate" type="date" value={editForm.trx_date || ""} onChange={(e) => setEditForm({ ...editForm, trx_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Order Type</Label>
                <Select value={editForm.order_type} onValueChange={(v) => setEditForm({ ...editForm, order_type: v })}>
                  <SelectTrigger data-testid="trx-edit-order-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dine_in">Dine In</SelectItem>
                    <SelectItem value="takeaway">Takeaway</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select></div>
              {editForm.order_type === "delivery" && (
                <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("deliveryApp")}</Label>
                  <Select value={editForm.delivery_app_id} onValueChange={(v) => setEditForm({ ...editForm, delivery_app_id: v })}>
                    <SelectTrigger data-testid="trx-edit-delivery-app" className="mt-1.5"><SelectValue placeholder="..." /></SelectTrigger>
                    <SelectContent>{deliveryApps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} (fee {a.fee_pct}%)</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditTrx(null)}>{t("cancel")}</Button>
              <Button data-testid="trx-edit-save-btn" onClick={saveEditTrx} className="bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {isAdmin ? (
        <Tabs defaultValue="kasir">
          <TabsList data-testid="pos-tabs" className="mb-4">
            <TabsTrigger value="kasir" data-testid="pos-tab-kasir">{t("posKasir")}</TabsTrigger>
            <TabsTrigger value="riwayat" data-testid="pos-tab-history">{t("history")}</TabsTrigger>
          </TabsList>
          <TabsContent value="kasir">{posGrid}</TabsContent>
          <TabsContent value="riwayat">{historyTab}</TabsContent>
        </Tabs>
      ) : posGrid}
      {editDialog}
    </>
  );
}
